import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { MultiSelectDropdown } from "@admin/components/atoms/Input/MultiSelect";
import { Option } from "@admin/components/atoms/Input/Select";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { addBulkShifts, dateToISO, LocationUser } from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation,useQueryClient } from "@tanstack/react-query";
import { addDays, addHours,getISOWeek, isValid } from "date-fns";
import { useCallback,useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface NewShiftWeeklyModalProps extends ModalProps {
  personnels: LocationUser[];
  selectedWeek: DateRange | undefined;
  companyId: string;
  companyLocationId: string;
}

export function NewShiftWeeklyModal({
  selectedWeek,
  personnels,
  companyId,
  companyLocationId,
  ...props
}: NewShiftWeeklyModalProps) {
  const { t, i18n } = useTranslation();
  const [selectedPersonnels, setSelectedPersonnels] = useState<Option[]>([]);
  const [days, setDays] = useState<Option[]>([]);
  const [personnelOptions, setPersonnelsOptions] = useState<Option[]>([]);
  // calculated time is a float number. e.g. 6.30 = 6.30 * 60 Minutes, 0.5 = 0.5 * 60 = 30 Minutes
  const [calculatedTime, setCalculatedTime] = useState(0);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const processDays = () => {
    const isNextDay = getHoursInNumber(endTime) < getHoursInNumber(startTime);
    return days.map((day, index) => {
      const dayIndex = parseInt(day?.value.toString());
      return {
        plannedStartTime: dateToISO(
          addHours(
            addDays(selectedWeek?.from as Date, dayIndex - 1),
            getHoursInNumber(startTime),
          ),
        ),
        plannedEndTime: dateToISO(
          addHours(
            addDays(
              selectedWeek?.from as Date,
              dayIndex + (isNextDay ? 0 : -1),
            ),
            getHoursInNumber(endTime),
          ),
        ),
      };
    });
  };
  const newWeeklyShiftSchema = z.object({
    startTime: z.string().min(1, t("shift.Start time is required")),
    endTime: z.string().min(1, t("shift.End time is required")),
  });
  type NewWeeklyShiftData = z.infer<typeof newWeeklyShiftSchema>;

  const onSubmit = () => {
    const payload = {
      companyId: companyId,
      companyLocationId: companyLocationId,
      users: selectedPersonnels
        .slice()
        .map((personnel) => personnel.value) as string[],
      shiftDates: processDays(),
    };
    mutation.mutate(payload);
  };

  const queryClient = useQueryClient();
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<NewWeeklyShiftData>({
    resolver: zodResolver(newWeeklyShiftSchema),
  });

  const mutation = useMutation({
    mutationFn: addBulkShifts,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["shifts", companyLocationId],
      });
      setStartTime("");
      setEndTime("");
      setSelectedPersonnels([]);
      setDays([]);
      reset();
      props?.onClose?.();
      toast.success(t("shift.Shifts have been planned"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      toast.error(t("shift.Unable to plan shifts"));
    },
  });

  const bindCredentials = useCallback(
    () =>
      personnels.map((personnel) => ({
        value: personnel.userId,
        label: `${personnel.firstName} ${personnel.lastName}`,
      })),
    [personnels],
  );

  useEffect(() => {
    const handleOptions = () => {
      if (personnels) {
        const options = bindCredentials();
        setPersonnelsOptions(options);
      }
    };
    handleOptions();
  }, [bindCredentials, personnels]);

  useEffect(() => {
    const timeDiff = calculateTimeDifference(startTime, endTime);
    setCalculatedTime(timeDiff);
  }, [startTime, endTime]);

  const dayOptions: Option[] = [
    { value: 1, label: t("shift.Monday") },
    { value: 2, label: t("shift.Tuesday") },
    { value: 3, label: t("shift.Wednesday") },
    { value: 4, label: t("shift.Thursday") },
    { value: 5, label: t("shift.Friday") },
    { value: 6, label: t("shift.Saturday") },
    { value: 7, label: t("shift.Sunday") },
  ];

  const getHoursInNumber = (time: string): number => {
    if (!time) return 0;
    const [hour, minute] = time.split(":").map(Number);
    return hour + minute / 60;
  };

  const calculateTimeDifference = (start: string, end: string): number => {
    if (!start || !end) return 0.0;

    const [startHour, startMinute] = start.split(":").map(Number);
    const [endHour, endMinute] = end.split(":").map(Number);

    const startDate = new Date();
    startDate.setHours(startHour, startMinute, 0);

    const endDate = new Date();
    endDate.setHours(endHour, endMinute, 0);

    if (endDate < startDate) {
      endDate.setDate(endDate.getDate() + 1);
    }
    const diffInMs = endDate.getTime() - startDate.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    return parseFloat(diffInHours.toFixed(2));
  };

  return (
    <Modal
      actionButtons={
        <>
          <Button variant="secondary" onClick={props.onClose}>
            {t("common.Discard")}
          </Button>
          <Button loading={mutation.isPending} onClick={handleSubmit(onSubmit)}>
            {t("shift.Plan Shifts")}
          </Button>
        </>
      }
      {...props}
      title={t("shift.Plan New Shifts to Week", {
        week: getISOWeek(selectedWeek?.from ?? ""),
        fromDate: isValid(selectedWeek?.from)
          ? Intl.DateTimeFormat(i18n.language, {
              day: "2-digit",
              month: "short",
            }).format(selectedWeek?.from)
          : t("shift.Invalid Date"),
        toDate: isValid(selectedWeek?.to)
          ? Intl.DateTimeFormat(i18n.language, {
              day: "2-digit",
              month: "short",
            }).format(selectedWeek?.to)
          : t("shift.Invalid Date"),
      })}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Label title={t("common.Select Personnel")}>
          <div className="flex flex-col gap-y-1 w-full">
            <MultiSelectDropdown
              variant="filled"
              placeholder={t("common.Select Personnel")}
              options={personnelOptions}
              selectedOptions={selectedPersonnels}
              onSelect={(selected) => setSelectedPersonnels(selected)}
            />
            {/* <ErrorMessage message={errors?.personnels?.message} /> */}
          </div>
        </Label>
        <Label title={t("shift.Select Days")}>
          <MultiSelectDropdown
            variant="filled"
            options={dayOptions}
            selectedOptions={days}
            onSelect={(selected) => {
              setDays(selected);
            }}
          />
        </Label>
        <Label title={t("shift.Shift Beginning and End")}>
          <div className="flex flex-row justify-between w-full gap-2">
            <div className="flex flex-col w-full gap-y-1">
              <Input
                {...register("startTime")}
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                type="time"
              />
              <ErrorMessage message={errors?.startTime?.message} />
            </div>
            <div className="flex flex-col w-full gap-y-1">
              <Input
                {...register("endTime")}
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                type="time"
              />
              <ErrorMessage message={errors?.endTime?.message} />
            </div>
          </div>
        </Label>
        <Label title={t("shift.Calculated Time")}>
          <Input value={calculatedTime} disabled />
        </Label>
      </form>
    </Modal>
  );
}
